import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    ContentCopy as CopyIcon,
    Search as SearchIcon,
} from "@mui/icons-material";

import {
    useSearchManifests,
    useManifestRouteTemplate,
} from "../../../../../hooks/useManifest";

const RouteTemplateSelector = ({ open, manifest, onApplyTemplate }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [pickedManifestId, setPickedManifestId] = useState("");

    const {
        data: manifestMatches = [],
        isFetching,
        refetch,
        error,
    } = useSearchManifests({
        q: searchTerm,
        limit: 10,
        enabled: false,
    });

    const {
        data: template,
        isLoading: isLoadingTemplate,
        error: templateError,
    } = useManifestRouteTemplate(
        pickedManifestId,
        open && Boolean(pickedManifestId)
    );

    useEffect(() => {
        if (!open) {
            setSearchTerm("");
            setPickedManifestId("");
        }
    }, [open]);

    useEffect(() => {
        setPickedManifestId("");
    }, [searchTerm]);

    const handleSearch = async () => {
        if (searchTerm.trim().length < 2) return;
        await refetch();
    };

    const handleApply = () => {
        if (!template?.paradas?.length) return;
        onApplyTemplate(template);
    };

    const safeMatches = Array.isArray(manifestMatches) ? manifestMatches : [];

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2.5,
                backgroundColor: "#fafafa",
            }}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <CopyIcon color="primary" />
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Copiar ruta desde otro manifiesto
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Reutiliza paradas y horarios ajustándolos a la fecha del
                            manifiesto actual.
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                        label="Buscar manifiesto"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="ID, nombre o referencia"
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                        }}
                        helperText="Escribe al menos 2 caracteres."
                    />

                    <Button
                        variant="outlined"
                        onClick={handleSearch}
                        disabled={isFetching || searchTerm.trim().length < 2}
                        sx={{ minWidth: 120, height: 40 }}
                    >
                        {isFetching ? <CircularProgress size={18} /> : "Buscar"}
                    </Button>
                </Stack>

                {error && (
                    <Alert severity="error" variant="outlined">
                        Error buscando manifiestos.
                    </Alert>
                )}

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                        select
                        label="Resultados"
                        value={pickedManifestId || ""}
                        onChange={(e) => setPickedManifestId(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={safeMatches.length === 0}
                        helperText={
                            safeMatches.length === 0
                                ? "No hay resultados seleccionables."
                                : "Seleccione un manifiesto de referencia."
                        }
                    >
                        <MenuItem value="">Seleccione</MenuItem>
                        {safeMatches.map((item) => (
                            <MenuItem
                                key={item.manid}
                                value={item.manid}
                                disabled={Number(item.manid) === Number(manifest?.manid)}
                            >
                                MN-{String(item.manid).padStart(6, "0")} ·{" "}
                                {item.maninf || "(sin nombre)"} · {item.manfec || "-"}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={
                            !pickedManifestId ||
                            isLoadingTemplate ||
                            !template?.paradas?.length
                        }
                        sx={{ minWidth: 150, height: 40 }}
                    >
                        {isLoadingTemplate ? <CircularProgress size={18} /> : "Cargar"}
                    </Button>
                </Stack>

                {templateError && (
                    <Alert severity="error" variant="outlined">
                        No se pudo cargar la plantilla del manifiesto seleccionado.
                    </Alert>
                )}

                {template?.paradas?.length > 0 && (
                    <Alert severity="success" variant="outlined">
                        Plantilla lista: {template.paradas.length} parada(s) disponibles.
                    </Alert>
                )}
            </Stack>
        </Paper>
    );
};

export default RouteTemplateSelector;